import { useState } from 'react';

export interface ScenarioStep {
  track: number;
  wait_seconds: number;
}

export interface BellScenario {
  day_start: ScenarioStep[];
  lesson_start: ScenarioStep[];
  lesson_end: ScenarioStep[];
  day_end: ScenarioStep[];
  lesson_voices: Array<{ start: number; end: number }>;
}

type Moment = 'day_start' | 'lesson_start' | 'lesson_end' | 'day_end';

interface Sound {
  track: number;
  name: string;
}

const MAX_STEPS = 6;
/** In a step: the current lesson's own announcement instead of a fixed track. */
const VOICE_START = -1;
const VOICE_END = -2;

/** The original sketch: 0001 → 0002; per lesson its voice → 0004, its end voice → 0005. */
export const DEFAULT_SCENARIO: BellScenario = {
  day_start: [
    { track: 1, wait_seconds: 0 },
    { track: 2, wait_seconds: 0 },
  ],
  lesson_start: [
    { track: VOICE_START, wait_seconds: 0 },
    { track: 4, wait_seconds: 0 },
  ],
  lesson_end: [
    { track: VOICE_END, wait_seconds: 0 },
    { track: 5, wait_seconds: 0 },
  ],
  day_end: [
    { track: VOICE_END, wait_seconds: 0 },
    { track: 5, wait_seconds: 0 },
  ],
  lesson_voices: Array.from({ length: 6 }, (_, i) => ({ start: 6 + i * 2, end: 7 + i * 2 })),
};

const MOMENTS: Array<{ key: Moment; title: string; hint: string }> = [
  { key: 'day_start', title: 'بداية اليوم', hint: 'مرة واحدة صباحًا، ثم مقاطع بداية الحصة الأولى' },
  {
    key: 'lesson_start',
    title: 'بداية كل حصة',
    hint: 'في توقيت «بعد انتهاء الجرس» يبدأ عداد الحصة بعد آخر مقطع هنا',
  },
  { key: 'lesson_end', title: 'نهاية حصة تليها استراحة', hint: 'بعدها تبدأ الاستراحة' },
  { key: 'day_end', title: 'نهاية آخر حصة', hint: 'بعدها ينتهي اليوم الدراسي' },
];

const code = (track: number): string => String(track).padStart(4, '0');

function stepName(track: number, sounds: Sound[]): string {
  if (track === VOICE_START) return 'إعلان بداية الحصة';
  if (track === VOICE_END) return 'إعلان نهاية الحصة';
  return sounds.find((s) => s.track === track)?.name ?? code(track);
}

function waitText(sec: number): string {
  return sec % 60 === 0 ? `${sec / 60} د` : `${sec} ث`;
}

/** A pause in seconds or minutes; stored in seconds. */
function WaitInput({ seconds, onChange }: { seconds: number; onChange: (seconds: number) => void }): JSX.Element {
  const [unit, setUnit] = useState<'sec' | 'min'>(seconds > 0 && seconds % 60 === 0 ? 'min' : 'sec');
  const shown = unit === 'min' ? Math.round((seconds / 60) * 100) / 100 : seconds;
  return (
    <span className="row" style={{ gap: 4, flexWrap: 'nowrap' }}>
      <span style={{ fontSize: 12.5, color: 'var(--muted)', whiteSpace: 'nowrap' }}>انتظار قبله</span>
      <input
        type="number"
        min={0}
        step={unit === 'min' ? 0.5 : 1}
        value={shown}
        onChange={(e) => {
          const v = Math.max(0, Number(e.target.value) || 0);
          onChange(Math.min(3600, Math.round(unit === 'min' ? v * 60 : v)));
        }}
        style={{ width: 72 }}
        aria-label="مدة الانتظار قبل المقطع"
      />
      <select
        value={unit}
        onChange={(e) => setUnit(e.target.value as 'sec' | 'min')}
        style={{ width: 'auto' }}
        aria-label="وحدة الانتظار"
      >
        <option value="sec">ثانية</option>
        <option value="min">دقيقة</option>
      </select>
    </span>
  );
}

function TrackSelect({
  value,
  onChange,
  sounds,
  voices,
  label,
}: {
  value: number;
  onChange: (track: number) => void;
  sounds: Sound[];
  /** Offer «the lesson's own announcement»; otherwise offer «none». */
  voices: boolean;
  label: string;
}): JSX.Element {
  const listed = value <= 0 || sounds.some((s) => s.track === value);
  return (
    <select
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      aria-label={label}
      style={{ flex: '1 1 220px', minWidth: 0 }}
    >
      {voices ? (
        <>
          <option value={VOICE_START}>🔊 إعلان بداية الحصة (حسب رقم الحصة)</option>
          <option value={VOICE_END}>🔊 إعلان نهاية الحصة (حسب رقم الحصة)</option>
        </>
      ) : (
        <option value={0}>بدون</option>
      )}
      {!listed ? <option value={value}>{code(value)} — غير موجود في مكتبة الأصوات</option> : null}
      <optgroup label="مكتبة الأصوات">
        {sounds.map((s) => (
          <option key={s.track} value={s.track}>
            {code(s.track)} — {s.name}
          </option>
        ))}
      </optgroup>
    </select>
  );
}

/**
 * What the bell plays at each moment of the day, clip after clip, with an
 * optional pause before each. Any library sound can be used, plus each
 * lesson's own announcement.
 */
export function ScenarioEditor({
  value,
  onChange,
  sounds,
}: {
  value: BellScenario;
  onChange: (scenario: BellScenario) => void;
  sounds: Sound[];
}): JSX.Element {
  const [open, setOpen] = useState(false);

  const setSteps = (moment: Moment, steps: ScenarioStep[]): void => onChange({ ...value, [moment]: steps });

  const move = (moment: Moment, index: number, by: number): void => {
    const steps = [...value[moment]];
    const target = index + by;
    if (target < 0 || target >= steps.length) return;
    [steps[index], steps[target]] = [steps[target]!, steps[index]!];
    setSteps(moment, steps);
  };

  const summary = (moment: Moment): string =>
    value[moment].length === 0
      ? 'بدون صوت'
      : value[moment]
          .map((s) => `${s.wait_seconds > 0 ? `(بعد ${waitText(s.wait_seconds)}) ` : ''}${stepName(s.track, sounds)}`)
          .join(' ← ');

  return (
    <div style={{ marginTop: 16 }}>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontWeight: 700 }}>سيناريو الأصوات</div>
          <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>
            ما يُشغَّل في كل لحظة من اليوم، مقطعًا بعد مقطع (يحتاج برنامج الجهاز 2.2 أو 3.2)
          </div>
        </div>
        <button type="button" className="ghost" onClick={() => setOpen((o) => !o)}>
          {open ? 'إخفاء التفاصيل' : 'تعديل السيناريو'}
        </button>
      </div>

      {!open ? (
        <ul className="scenario-summary">
          {MOMENTS.map((m) => (
            <li key={m.key}>
              <strong>{m.title}:</strong> {summary(m.key)}
            </li>
          ))}
        </ul>
      ) : (
        <>
          {MOMENTS.map((m) => (
            <div key={m.key} className="scenario-moment">
              <div className="scenario-title">
                {m.title}
                <span>{m.hint}</span>
              </div>
              {value[m.key].length === 0 ? (
                <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 6 }}>بدون صوت</div>
              ) : (
                <ol className="scenario-steps">
                  {value[m.key].map((step, i) => (
                    <li key={`${m.key}-${i}-${step.track}`}>
                      <span className="num scenario-index">{i + 1}</span>
                      <TrackSelect
                        value={step.track}
                        sounds={sounds}
                        voices
                        label={`${m.title}: المقطع ${i + 1}`}
                        onChange={(track) =>
                          setSteps(
                            m.key,
                            value[m.key].map((s, j) => (j === i ? { ...s, track } : s)),
                          )
                        }
                      />
                      <WaitInput
                        seconds={step.wait_seconds}
                        onChange={(wait_seconds) =>
                          setSteps(
                            m.key,
                            value[m.key].map((s, j) => (j === i ? { ...s, wait_seconds } : s)),
                          )
                        }
                      />
                      <span className="row" style={{ gap: 2 }}>
                        <button
                          type="button"
                          className="ghost"
                          disabled={i === 0}
                          onClick={() => move(m.key, i, -1)}
                          aria-label="تقديم المقطع"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          className="ghost"
                          disabled={i === value[m.key].length - 1}
                          onClick={() => move(m.key, i, 1)}
                          aria-label="تأخير المقطع"
                        >
                          ↓
                        </button>
                        <button
                          type="button"
                          className="ghost"
                          onClick={() => setSteps(m.key, value[m.key].filter((_, j) => j !== i))}
                          aria-label="حذف المقطع"
                        >
                          ✕
                        </button>
                      </span>
                    </li>
                  ))}
                </ol>
              )}
              {value[m.key].length < MAX_STEPS ? (
                <button
                  type="button"
                  className="ghost"
                  onClick={() => setSteps(m.key, [...value[m.key], { track: sounds[0]?.track ?? 4, wait_seconds: 0 }])}
                >
                  + إضافة مقطع
                </button>
              ) : null}
            </div>
          ))}

          <div className="scenario-moment">
            <div className="scenario-title">
              إعلان كل حصة
              <span>ما يُشغَّل مكان «إعلان بداية الحصة» و«إعلان نهاية الحصة» لكل حصة</span>
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>الحصة</th>
                    <th>إعلان البداية</th>
                    <th>إعلان النهاية</th>
                  </tr>
                </thead>
                <tbody>
                  {value.lesson_voices.map((v, i) => (
                    <tr key={i}>
                      <td className="num">{i + 1}</td>
                      <td>
                        <TrackSelect
                          value={v.start}
                          sounds={sounds}
                          voices={false}
                          label={`إعلان بداية الحصة ${i + 1}`}
                          onChange={(start) =>
                            onChange({
                              ...value,
                              lesson_voices: value.lesson_voices.map((x, j) => (j === i ? { ...x, start } : x)),
                            })
                          }
                        />
                      </td>
                      <td>
                        <TrackSelect
                          value={v.end}
                          sounds={sounds}
                          voices={false}
                          label={`إعلان نهاية الحصة ${i + 1}`}
                          onChange={(end) =>
                            onChange({
                              ...value,
                              lesson_voices: value.lesson_voices.map((x, j) => (j === i ? { ...x, end } : x)),
                            })
                          }
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <button type="button" className="ghost" style={{ marginTop: 8 }} onClick={() => onChange(DEFAULT_SCENARIO)}>
            استعادة السيناريو الأصلي
          </button>
        </>
      )}
    </div>
  );
}
